/**
 * THE ICON SET — shared with mobile, like colour and unlike size.
 *
 * **Why icons are a token and not a component.** An icon is geometry: a path in
 * a 24×24 box. That geometry has to be identical on web, desktop, iOS and
 * Android for the same reason a colour does — a bell that means "alerts" on the
 * desktop cannot be a different bell on the phone. What differs per platform is
 * how big it is drawn and what draws it (`<svg>` in a browser,
 * `react-native-svg` on a device), and neither of those belongs here.
 *
 * So this file holds path data and nothing else. No React, no DOM, no styling.
 *
 * **The drawing convention, which every icon must follow:**
 *
 * - A **24×24** viewBox. Not 16, not 20 — one box, so a size change is a single
 *   number at the render site rather than a per-icon correction.
 * - **Stroked, not filled**, at `strokeWidth` 1.6, with round caps and joins.
 *   The value is in `ICON_STROKE` below and is shared deliberately: stroke width
 *   scales with the viewBox, so 1.6 in a 24-box looks the same at 18px on a
 *   desktop rail and 24px in a phone tab bar. A filled icon among stroked ones
 *   reads as heavier at every size and is the fastest way to make a set look
 *   assembled from two libraries.
 * - **`currentColor`**, always. An icon never names a colour; it inherits from
 *   the text around it, which is what makes the whole set follow a theme change
 *   for free.
 * - Multiple sub-paths are separated by a space inside one `d` string. Splitting
 *   them into separate `<path>` elements would let a renderer fill the gaps.
 *
 * **Adding one:** add it here, and it is available on every platform at once.
 * Adding it to a component instead is how the phone ends up with a different
 * bell — which is exactly the state this file replaced, where five navigation
 * icons lived inline in `App.tsx`, nine drawing tools lived in
 * `DrawingToolbar.tsx`, and mobile had four placeholder text glyphs.
 */

/** Stroke width in viewBox units. Shared: it scales with the box, so it holds at every size. */
export const ICON_STROKE = 1.6;

/** Every icon is drawn in this box. */
export const ICON_VIEWBOX = '0 0 24 24';

export const icons = {
  // --- navigation ----------------------------------------------------------
  // The five sections, plus the mobile tab bar. `chart` is a price path rather
  // than a candlestick on purpose: at 18px a candlestick is four grey smudges.
  chart: 'M3 20 L9 9 L14 15 L21 4',
  journal: 'M4 4h16v16H4z M8 8h8 M8 12h8 M8 16h5',
  analytics: 'M4 20V10 M10 20V4 M16 20v-8 M22 20H2',
  /**
   * Today. Unequal blocks, so it reads as an overview rather than as `table`,
   * which is a ruled grid. The phone's first tab: day P&L, open positions and
   * rule state on one screen.
   */
  today: 'M4 4h7v7H4z M13 4h7v5h-7z M13 13h7v7h-7z M4 15h7v5H4z',
  /**
   * Everything that does not fit five tabs. Three dots, drawn the way `target`
   * draws its centre — a zero-length segment with a round cap.
   */
  more: 'M5 12h.01 M12 12h.01 M19 12h.01',
  /** A drag grip: two columns of dots, the universal "pick this up". */
  drag: 'M9 6h.01 M9 12h.01 M9 18h.01 M15 6h.01 M15 12h.01 M15 18h.01',
  /**
   * Reports. A table, not a second bar chart: the rail already has one for
   * analytics, and two identical marks are two destinations the eye cannot
   * tell apart.
   */
  table: 'M3 5h18v14H3z M3 10h18 M3 15h18 M9 5v14',
  /**
   * Strategies. A target, because a strategy is a claim about a repeatable
   * setup — and because `journal` was already taken by the journal.
   */
  target: 'M12 21a9 9 0 100-18 9 9 0 000 18z M12 16a4 4 0 100-8 4 4 0 000 8z M12 12h.01',
  /** The daily loop. A calendar, distinct from the settings gear it used to share. */
  calendar: 'M4 6h16v15H4z M4 11h16 M8 3v4 M16 3v4',
  alerts: 'M12 3a6 6 0 00-6 6c0 5-2 6-2 6h16s-2-1-2-6a6 6 0 00-6-6z M10 21h4',
  /**
   * Fundamentals — macro, the world outside the chart.
   *
   * A globe rather than a newspaper: the tab carries central banks and national
   * statistics as much as headlines, and a rolled-up newspaper reads as
   * "articles" rather than "the wider market". Two ellipses and an equator,
   * which stays legible at 18px where a wireframe of meridians turns to mush.
   */
  globe: 'M12 21a9 9 0 100-18 9 9 0 000 18z M3 12h18 M12 3a13 13 0 010 18 13 13 0 010-18z',
  settings: 'M12 15a3 3 0 100-6 3 3 0 000 6z M4 12h2 M18 12h2 M12 4v2 M12 18v2',

  // --- drawing tools -------------------------------------------------------
  // Each one draws the mark it makes. A trend line icon that is not a diagonal
  // line makes the user read the tooltip every time.
  cursor: 'M5 3l14 8-6 1.5L10 19z',
  trendline: 'M4 20L20 4',
  horizontal: 'M3 12h18',
  vertical: 'M12 3v18',
  ray: 'M4 20L20 4 M20 4h-5 M20 4v5',
  rectangle: 'M4 6h16v12H4z',
  fib: 'M3 5h18 M3 10h18 M3 14h18 M3 19h18',
  measure: 'M4 20L20 4 M4 20v-5 M4 20h5',
  text: 'M5 6h14 M12 6v13',

  // --- replay --------------------------------------------------------------
  replay: 'M4 12a8 8 0 108-8 M4 12V7 M4 12h5 M11 9v6l5-3z',
  play: 'M7 4v16l13-8z',
  pause: 'M8 5v14 M16 5v14',
  step: 'M6 4l10 8-10 8z M18 4v16',

  // --- actions -------------------------------------------------------------
  close: 'M5 5l14 14 M19 5L5 19',
  trash: 'M4 7h16 M9 7V4h6v3 M6 7l1 13h10l1-13',
  plus: 'M12 5v14 M5 12h14',
  check: 'M4 12l5 5L20 6',
  chevronRight: 'M9 5l7 7-7 7',
  chevronLeft: 'M15 5l-7 7 7 7',
  chevronDown: 'M5 9l7 7 7-7',
  chevronUp: 'M5 15l7-7 7 7',
  refresh: 'M20 12a8 8 0 11-2.3-5.7 M20 4v4h-4',
  externalLink: 'M14 4h6v6 M20 4l-9 9 M19 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1h5',

  // --- domain --------------------------------------------------------------
  // Concepts this product has that a generic icon set does not.
  /** Devices — the licensing screen. CME's billable unit is the device. */
  device: 'M7 3h10a1 1 0 011 1v16a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z M10 18h4',
  /** Security and sessions. The aegis was a shield; the product is named for one. */
  shield: 'M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6z',
  /** A connected broker. Read-only by design — there is no order icon, deliberately. */
  broker: 'M9 15l6-6 M8.5 12.5L6 15a3.5 3.5 0 005 5l2.5-2.5 M15.5 11.5L18 9a3.5 3.5 0 00-5-5l-2.5 2.5',
  /** Tax export. */
  receipt: 'M6 3h12v18l-3-2-3 2-3-2-3 2z M9 8h6 M9 12h6',
  /** The AI layer. A diamond, matching the ◆ that marks the analysis panel. */
  ai: 'M12 3l3 6 6 3-6 3-3 6-3-6-6-3 6-3z',

  // --- status --------------------------------------------------------------
  warning: 'M12 4l9 16H3z M12 10v4 M12 17v.5',
  info: 'M12 21a9 9 0 100-18 9 9 0 000 18z M12 11v5 M12 7.5v.5',
  lock: 'M6 11h12v9H6z M9 11V8a3 3 0 016 0v3',
  // Pinning a symbol. Stroked like everything else — a filled star would read
  // as heavier than the row it sits in, and the "on" state is carried by
  // colour, which follows the theme.
  star: 'M12 3.5l2.6 5.3 5.9.9-4.25 4.15 1 5.85L12 16.95 6.75 19.7l1-5.85L3.5 9.7l5.9-.9z',
} as const;

export type IconName = keyof typeof icons;

/** Every icon name, for a picker or a test that walks the set. */
export const iconNames = Object.keys(icons) as IconName[];

/** The path data for an icon. Typed, so a mistyped name is a build error. */
export function iconPath(name: IconName): string {
  return icons[name];
}
